import { mdiCogOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  DollarCircleBlendSVGIcon,
  EyeOffOutlineSVGIcon,
  EyeOutlineSVGIcon
} from "components/SVGIcons";
import { WALLET_BALANCE_LIST } from "constants/home";
import { routes } from "constants/routes";
import { formatNumber } from "helpers/formatNumber";
import { IWalletBalance } from "models/Home";
import { selectCardSettings, selectWalletBalance } from "store/home/selector";
import styles from "./style.module.scss";
import { selectFactoringProfile } from "../../../../../../store/user/selector";

interface IProps {
  data: IWalletBalance;
  className?: string;
}

function DefaultCard({ data, className }: Readonly<IProps>) {
  const navigate = useNavigate();
  const walletBalance = useSelector(selectWalletBalance);
  const [isShow, setIsShow] = useState(true);

  const goToSettings = () => {
    navigate(routes.HOME_CARD_SETTINGS);
  };

  const goToWallet = () => {
    navigate(routes.HAULPAY_BROKER_WALLET);
  };

  return walletBalance ? (
    <div className={clsx(styles.allowedStatusCard, className)}>
      <span className="text-sm">{data.title}</span>
      <div className="flex items-end">
        <span className="text-4xl font-bold">
          {isShow ? formatNumber(walletBalance[data.value] ?? 0, 2).split(".")[0] : "$***"}
        </span>
        <span className="text-xl">{`.${isShow ? formatNumber(walletBalance[data.value] ?? 0, 2).split(".")[1] : "**"}`}</span>
        <button onClick={() => setIsShow(!isShow)}>
          {isShow ? (
            <EyeOffOutlineSVGIcon className="ml-3.5 mb-1.5" />
          ) : (
            <EyeOutlineSVGIcon className="ml-3.5 mb-2" />
          )}
        </button>
      </div>
      <DollarCircleBlendSVGIcon className={styles.cardIcon} />

      <button className={styles.settingsButton} onClick={goToSettings}>
        <Icon path={mdiCogOutline} />
      </button>

      <button className={styles.viewDetailsBackground} onClick={goToWallet}>
        <span className={styles.viewDetailsButton}>View Details</span>
      </button>
    </div>
  ) : null;
}

export default function HomeAllowedStatusCard() {
  const cardSettings = useSelector(selectCardSettings);
  const factoringProfile = useSelector(selectFactoringProfile);
  const allowSelfFinanceFr = factoringProfile?.allow_self_finance_fr ?? false;
  const RESERVE_BALANCE = "show_reserve_balance";
  const WALLET_BALANCE = "show_wallet_balance";
  const cardList = (cardSettings as IWalletBalance[]).filter((item) => {
    if (item.value === RESERVE_BALANCE) return false;
    if (item.value === WALLET_BALANCE && !allowSelfFinanceFr) return false;
    return item.show;
  });

  if (!cardList) return null;
  return cardList.length > 1 ? (
    <Swiper
      modules={[Navigation, Pagination, Autoplay, EffectFade]}
      effect={"fade"}
      loop={true}
      spaceBetween={30}
      centeredSlides={true}
      autoplay={{
        delay: 2000,
        stopOnLastSlide: false,
        disableOnInteraction: false
      }}
      pagination={{
        clickable: true
      }}
      className="fade max-w-full mb-5 !overflow-visible home-status-card">
      {WALLET_BALANCE_LIST.filter(
        (card) => (cardSettings as IWalletBalance[]).find((item) => item.value === card.value)?.show
      ).map((item) => (
        <SwiperSlide key={item.id}>
          <DefaultCard data={item} />
        </SwiperSlide>
      ))}
    </Swiper>
  ) : (
    <>
      {WALLET_BALANCE_LIST.filter(
        (card) => (cardSettings as IWalletBalance[]).find((item) => item.value === card.value)?.show
      ).map((item) => (
        <DefaultCard key={item.id} data={item} className="mb-5" />
      ))}
    </>
  );
}
